import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const FieldInfo = ({ field, className = "" }) => {
  return (
    <>
      {field.state.meta.isTouched && field.state.meta.errors?.length > 0 && (
        <ul
          className={`text-sm text-red-600 list-disc pl-5 space-y-1 ${className}`}
        >
          {field.state.meta.errors.map((err, i) => (
            <li key={i}>{err.message}</li>
          ))}
        </ul>
      )}
      {field.state.isValidating && (
        <span className={`text-gray-500 text-sm ${className}`}>
          Validating...
        </span>
      )}
    </>
  );
};

export const DynamicForm = ({
  form,
  formConfig,
  buttonText,
  isLoading,
  onCancel,
}) => {
  // track show/hide for password fields by field key
  const [showPassword, setShowPassword] = useState({});
  const handleValueConversion = (value, valueType) => {
    if (value === "" || value === null || value === undefined) return null; // Handle empty selection
    if (valueType === "boolean") return value === "true"; // Convert "true"/"false" to boolean
    if (valueType === "number") return isNaN(value) ? null : Number(value); // Convert string number to number
    return value; // Keep as is for strings
  };
  return (
    <form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // handleSubmit gathers current form values and passes { value } to your onSubmit
        form.handleSubmit();
      }}
    >
      {Object.entries(formConfig).map(
        ([
          key,
          {
            required,
            label,
            isLoadingOptions,
            type,
            placeholder,
            options,
            valueType,
            listeners,
            hidden,
            multi,
          },
        ]) => (
          <form.Field
            key={key}
            name={key}
            {...(listeners ? { listeners } : {})}
          >
            {(field) => {
              // console.log("Field: ", field); // ✅ safely logs each field

              return (
                <div className={`mb-2 ${hidden ? "hidden" : ""}`}>
                  <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400">
                    {label}{" "}
                    {!required && <span className="text-xs">(Optional)</span>}
                    {required && <span className="text-red-500">*</span>}
                  </label>

                  {/* Select */}
                  {type === "select" &&
                    (isLoadingOptions ? (
                      <p>Loading options...</p>
                    ) : options && options.length > 0 ? (
                      <select
                        value={field.state.value ?? ""}
                        onBlur={field.handleBlur}
                        onChange={(e) => {
                          const val = handleValueConversion(
                            e.target.value,
                            valueType
                          );
                          field.handleChange(val);
                        }}
                        className="mt-1 h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-base text-zinc-900 outline-hidden focus:ring-2 focus:ring-black/5 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:focus:ring-white/5 sm:text-sm"
                      >
                        <option value="" disabled>
                          {placeholder || "Select an option"}
                        </option>
                        {options.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p>No options available</p>
                    ))}

                  {/* Multi-select */}
                  {type === "multi-select" && options && (
                    <div className="flex flex-wrap gap-2">
                      {options.map((option) => {
                        const isMultiple = multi ?? true;
                        const checked = isMultiple
                          ? field.state.value?.includes(option.id) ?? false
                          : field.state.value === option.id;

                        return (
                          <label
                            key={option.id}
                            className={`flex items-center space-x-2 ${
                              option.isDisabled
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              value={option.id}
                              disabled={option.isDisabled}
                              checked={checked}
                              onChange={(e) => {
                                if (isMultiple) {
                                  const selected = new Set(
                                    field.state.value ?? []
                                  );
                                  if (e.target.checked) selected.add(option.id);
                                  else selected.delete(option.id);
                                  field.handleChange(Array.from(selected));
                                } else {
                                  field.handleChange(
                                    e.target.checked ? option.id : null
                                  );
                                }
                              }}
                            />
                            <span>{option.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* Textarea */}
                  {type === "textarea" && (
                    <textarea
                      placeholder={placeholder}
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 p-2 bg-white dark:bg-zinc-800 text-gray-900 dark:text-gray-100 focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm resize-none"
                    />
                  )}

                  {/* Checkbox */}
                  {type === "checkbox" && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.state.value ?? false}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.checked)}
                      />
                      <span className="ml-2">{label}</span>
                    </div>
                  )}

                  {/* Radio */}
                  {type === "radio" &&
                    options &&
                    options.map((option) => (
                      <label
                        key={option.id}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="radio"
                          name={key}
                          value={option.id}
                          checked={field.state.value === option.id}
                          onBlur={field.handleBlur}
                          onChange={() => field.handleChange(option.id)}
                        />
                        <span>{option.name}</span>
                      </label>
                    ))}

                  {/* File */}
                  {type === "file" && (
                    <input
                      type="file"
                      onBlur={field.handleBlur}
                      autoComplete="off"
                      onChange={(e) => field.handleChange(e.target.files[0])}
                    />
                  )}

                  {/* Number */}
                  {type === "number" && (
                    <input
                      type="number"
                      placeholder={placeholder}
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) =>
                        field.handleChange(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      className="mt-1 h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-base text-zinc-900 outline-hidden focus:ring-2 focus:ring-black/5 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:focus:ring-white/5 sm:text-sm"
                    />
                  )}

                  {/* Date */}
                  {type === "date" && (
                    <input
                      type="date"
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 p-2 bg-white dark:bg-zinc-800 text-gray-900 dark:text-gray-100 focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                  )}

                  {/* Email */}
                  {type === "email" && (
                    <input
                      type="email"
                      placeholder={placeholder}
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="mt-1 h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-base text-zinc-900 outline-hidden focus:ring-2 focus:ring-black/5 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:focus:ring-white/5 sm:text-sm"
                    />
                  )}

                  {/* Default input */}
                  {["text", "password"].includes(type) && (
                    <div className="relative">
                      <input
                        type={
                          type === "password" && showPassword[key]
                            ? "text"
                            : type
                        }
                        autoComplete={
                          key === "username" || key === "password"
                            ? "new-password"
                            : "off"
                        }
                        autoCorrect="off"
                        spellCheck="false"
                        placeholder={placeholder}
                        value={field.state.value ?? ""}
                        onBlur={field.handleBlur}
                        onChange={(e) => {
                          let val = e.target.value;
                          const normalizeFn = formConfig[key]?.normalize;
                          if (normalizeFn) val = normalizeFn(val);
                          field.handleChange(val);
                        }}
                        className="mt-1 h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 pr-10 text-base text-zinc-900 outline-hidden focus:ring-2 focus:ring-black/5 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:focus:ring-white/5 sm:text-sm"
                      />

                      {type === "password" && (
                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword((prev) => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                          aria-label={
                            showPassword[key]
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          {showPassword[key] ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  <FieldInfo field={field} />
                </div>
              );
            }}
          </form.Field>
        )
      )}

      {/* Submit & Cancel Buttons */}
      <div className="flex justify-end mt-2 gap-2">
        <form.Subscribe
          selector={(state) => [
            state.isDirty,
            state.isValid,
            state.isSubmitting,
            state,
          ]}
        >
          {([isDirty, isValid, isSubmitting, fullState]) => {
            // console.log("Full form state 👉", fullState);
            return (
              <button
                type="submit"
                className="inline-flex items-center justify-center self-end rounded-lg bg-green-500 hover:bg-green-600 px-4 py-2 text-sm font-medium text-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] text-center"
                disabled={!isDirty || !isValid || isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? "Processing..." : buttonText}
              </button>
            );
          }}
        </form.Subscribe>

        <button
          type="button"
          onClick={onCancel}
          className="mr-2 rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-zinc-100 hover:bg-red-600"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};
